import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import indexedDbFileStorage from '../utils/indexedDbFileStorage';
import './SaveMessageModal.css';

/**
 * Save Message Modal Component
 * Allows users to save AI assistant messages to the Mia Platform marketplace
 */
const SaveMessageModal = ({
  isOpen,
  onClose,
  onSave,
  messageText = '',
  tenantId = null,
  projectId = null,
  serviceName = null
}) => {
  const [saveMode, setSaveMode] = useState('marketplace'); // 'marketplace' or 'file'
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [fileName, setFileName] = useState('');
  const [filePath, setFilePath] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSaveMode('marketplace');
      setName('');
      setDescription('');
      setCategory('');
      setFileName('');
      setFilePath('');
      setError(null);
      setIsSaving(false);
    }
  }, [isOpen]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      if (saveMode === 'marketplace') {
        // Validation for marketplace save
        if (!name.trim()) {
          setError('Please enter a message name');
          setIsSaving(false);
          return;
        }

        if (!description.trim()) {
          setError('Please enter a description');
          setIsSaving(false);
          return;
        }

        if (!tenantId) {
          setError('No tenant selected. Please select a tenant in Canvas mode.');
          setIsSaving(false);
          return;
        }

        await onSave({
          name: name.trim(),
          description: description.trim(),
          category: category.trim() || 'General',
          messageText,
          tenantId
        });
      } else {
        // Validation for file save
        if (!fileName.trim()) {
          setError('Please enter a file name');
          setIsSaving(false);
          return;
        }

        if (!projectId || !serviceName) {
          setError('No project or service selected. Please select a project in Canvas mode.');
          setIsSaving(false);
          return;
        }

        // Build full file path
        const fullPath = filePath.trim() 
          ? `${filePath.trim()}/${fileName.trim()}`
          : fileName.trim();

        // Save to IndexedDB
        await indexedDbFileStorage.saveFile(
          projectId,
          serviceName,
          fullPath,
          messageText,
          { isNew: true }
        );

        // Show success message
        if (onSave.successCallback) {
          onSave.successCallback(`File "${fullPath}" saved successfully!`);
        }
      }
      onClose();
    } catch (err) {
      console.error('Error saving message:', err);
      setError(err.message || 'Failed to save message');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSave();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="save-message-modal-overlay" onClick={onClose}>
      <div className="save-message-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="save-message-header">
          <h2 className="save-message-title">
            <span className="save-message-icon">
              <svg fill="none" height="24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="24">
                <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg>
            </span>
            Save Message
          </h2>
          <button
            className="save-message-close"
            onClick={onClose}
            type="button"
            aria-label="Close"
            disabled={isSaving}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="save-message-content">
          <p className="save-message-description">
            Save this message to your Mia Platform marketplace or as a file in your project.
          </p>

          {/* Save Mode Toggle */}
          <div className="save-message-field">
            <label>Save As</label>
            <div className="save-mode-toggle">
              <button
                className={`mode-button ${saveMode === 'marketplace' ? 'active' : ''}`}
                onClick={() => setSaveMode('marketplace')}
                type="button"
                disabled={isSaving}
              >
                <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
                Marketplace Item
              </button>
              <button
                className={`mode-button ${saveMode === 'file' ? 'active' : ''}`}
                onClick={() => setSaveMode('file')}
                type="button"
                disabled={isSaving}
              >
                <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
                  <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
                  <polyline points="13 2 13 9 20 9"/>
                </svg>
                Project File
              </button>
            </div>
          </div>

          {saveMode === 'marketplace' ? (
            <>
              {/* Name Input */}
              <div className="save-message-field">
            <label htmlFor="message-name">
              Message Name <span className="required">*</span>
            </label>
            <input
              id="message-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g., API Integration Guide"
              disabled={isSaving}
              autoFocus
            />
          </div>

          {/* Description Input */}
          <div className="save-message-field">
            <label htmlFor="message-description">
              Description <span className="required">*</span>
            </label>
            <textarea
              id="message-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Detailed explanation of REST API integration patterns"
              disabled={isSaving}
              rows={3}
            />
          </div>

              {/* Category Input (Optional) */}
              <div className="save-message-field">
                <label htmlFor="message-category">
                  Category <span className="optional">(optional)</span>
                </label>
                <input
                  id="message-category"
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g., Development, Architecture, Testing"
                  disabled={isSaving}
                />
              </div>
            </>
          ) : (
            <>
              {/* File Name Input */}
              <div className="save-message-field">
                <label htmlFor="file-name">
                  File Name <span className="required">*</span>
                </label>
                <input
                  id="file-name"
                  type="text"
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="e.g., response.txt, notes.md"
                  disabled={isSaving}
                  autoFocus
                />
              </div>

              {/* File Path Input (Optional) */}
              <div className="save-message-field">
                <label htmlFor="file-path">
                  Path <span className="optional">(optional)</span>
                </label>
                <input
                  id="file-path"
                  type="text"
                  value={filePath}
                  onChange={(e) => setFilePath(e.target.value)}
                  placeholder="e.g., docs, src/notes"
                  disabled={isSaving}
                />
                <small className="field-hint">Leave empty to save in root directory</small>
              </div>
            </>
          )}

          {/* Message Text Preview */}
          <div className="save-message-field">
            <label>Message Text Preview</label>
            <div className="message-text-preview">
              {messageText || 'No message text'}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="save-message-error">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="save-message-footer">
          <button
            className="save-message-cancel"
            onClick={onClose}
            type="button"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="save-message-save"
            onClick={handleSave}
            type="button"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : (saveMode === 'marketplace' ? 'Save to Marketplace' : 'Save as File')}
          </button>
        </div>

        {/* Keyboard hint */}
        <div className="save-message-hint">
          Press <kbd>⌘</kbd>/<kbd>Ctrl</kbd> + <kbd>Enter</kbd> to save
        </div>
      </div>
    </div>,
    document.body
  );
};

SaveMessageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  messageText: PropTypes.string,
  tenantId: PropTypes.string,
  projectId: PropTypes.string,
  serviceName: PropTypes.string
};

export default SaveMessageModal;
